import React from 'react';
import Badge from 'components/ui/Badge';
import {Tooltip} from 'components/ui/Tooltip';
import {MegaphoneOff, Check} from 'lucide-react';
import {twMerge} from 'tailwind-merge';

export const formatRelatedData = (relation, field = 'title') => {
	if (!relation) return '-';
	if (relation.data) return relation.data[field] || '-';
	return relation[field] || '-';
};

export const formatPersonData = person => {
	const personData = person?.data || person;
	if (!personData) return '-';
	const {firstName, lastName} = personData;
	return `${firstName} ${lastName}`.trim() || '-';
};

export const formatBuildingData = building => {
	const buildingData = building?.data || building;
	if (!buildingData) return '-';
	const {address, zip, city, manufacturingYear} = buildingData;
	const buildingAddress = [address, zip, city].filter(Boolean).join(', ');
	return manufacturingYear
		? `${buildingAddress} (${manufacturingYear})`
		: buildingAddress || '-';
};

export const formatTeamData = team => {
	const teamData = team?.data || team;
	if (!teamData) return '-';
	const organizationTitle =
		teamData.organization?.data?.title || teamData.organization?.title;
	return organizationTitle
		? `${teamData.title} - ${organizationTitle}`
		: teamData.title || '-';
};

export const formatAreaData = area => {
	const areaData = area?.data || area;
	if (!areaData) return '-';
	const {title, subtitle} = areaData;
	return title + (subtitle ? ` (${subtitle})` : '') || '-';
};

/**
 * Formats array or single values using a provided formatter function
 * Handles both strings and React components intelligently
 *
 * @param {*} value - Single value or array to format
 * @param {Function} [formatter] - Function to format each item. If not provided, uses default field extraction
 * @param {string} [separator=', '] - Separator for joining formatted values (ignored for React elements)
 * @returns {string|Array<ReactElement>} - Formatted string or array of React elements
 */
export const formatArrayData = (value, formatter, separator = ', ') => {
	if (!value) return '-';

	// Default formatter extracts common fields
	const defaultFormatter = item => {
		const data = item?.data || item;
		if (typeof data === 'string') return data;
		return data?.title || data?.name || '-';
	};

	const activeFormatter = formatter || defaultFormatter;

	// Handle arrays
	if (Array.isArray(value)) {
		if (value.length === 0) return '-';
		const formatted = value.map(v => activeFormatter(v)).filter(Boolean);

		// Check if any result is a React element (object with $$typeof)
		const hasReactElements = formatted.some(
			v => v && typeof v === 'object' && v.$$typeof,
		);

		if (hasReactElements) {
			// Return array of React elements (React can render arrays)
			return formatted;
		}

		// Return joined string for non-React values
		return formatted.join(separator);
	}

	// Handle single value
	return activeFormatter(value);
};

export const formatBoolean = value => {
	if (value === true) return <Check className="size-3.5 " strokeWidth={2.5} />;
	if (value === false) return '-';
	return '-';
};

/**
 * Creates a client formatter function with optional badges
 * @param {Object} config - Configuration object
 * @param {boolean} [config.showMarketingBadge] - Show marketing ban badge
 * @param {boolean} [config.showRobinsonBadge] - Show robinson date badge
 * @param {Object} intl - React Intl object for formatting messages
 * @returns {Function} Formatter function that takes a client object
 */
export const createClientFormatter = (config = {}, intl) => {
	const {
		showBadges = true,
		badgesAreIconOnly = false,
		className = '',
		iconClassName = '',
		badgeClassName = '',
	} = config;

	const formatClient = client => {
		const clientData = client?.data || client;
		if (!clientData) return '-';

		const clientName = formatPersonData(client);

		if (!showBadges) {
			return clientName;
		}

		const badges = [];

		const badgeClasses = twMerge('gap-1.5', badgeClassName);
		const iconClasses = twMerge('size-3.5', iconClassName);

		if (showBadges && clientData.marketingPrevented) {
			const badgeContent = (
				<Badge variant="orange" className={badgeClasses}>
					<MegaphoneOff className={iconClasses} />
					{badgesAreIconOnly ? null : intl.formatMessage({id: 'Marketing ban'})}
				</Badge>
			);
			badges.push(
				badgesAreIconOnly ? (
					<Tooltip key="marketing" trigger={badgeContent} placement="top" size="xs">
						{intl.formatMessage({id: 'Marketing ban'})}
					</Tooltip>
				) : (
					<React.Fragment key="marketing">{badgeContent}</React.Fragment>
				),
			);
		}

		if (showBadges && clientData.robinsonActive) {
			const badgeContent = (
				<Badge variant="amber" className={badgeClasses}>
					<MegaphoneOff className={iconClasses} />
					{badgesAreIconOnly ? null : intl.formatMessage({id: 'Robinson'})}
				</Badge>
			);
			badges.push(
				badgesAreIconOnly ? (
					<Tooltip key="robinson" trigger={badgeContent} placement="top" size="xs">
						{intl.formatMessage({id: 'Robinson'})}
					</Tooltip>
				) : (
					<React.Fragment key="robinson">{badgeContent}</React.Fragment>
				),
			);
		}

		if (badges.length === 0) {
			return clientName;
		}

		return (
			<div
				className={twMerge('flex items-center gap-y-0.5 gap-x-0.5 flex-wrap', className)}
			>
				<span className="truncate" title={clientName}>
					{clientName}
				</span>
				{badges}
			</div>
		);
	};

	formatClient.displayName = 'FormatClient';
	return formatClient;
};
